import { Box, Typography, Divider, Skeleton, useTheme } from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import strings from "global/constants/StringConstants";
import { useEffect, useState } from "react";
import {
  formatCount,
  formatLimit,
  isUnlimitedLimit,
  isTruthy,
} from "helpers/methods";
import { AccountDetails, AppPlan } from "models/interfaces";
import { fetchAccountDetails, fetchCurrentPlan } from "Services/CommonServices";
import { useAppSelector } from "redux/hooks";
import { selectAccount } from "redux/authSlice";
import { hasAccessTo } from "layouts/Config";
import UnlimitedLabel from "global/components/UnlimitedLabel/UnlimitedLabel";
import { planUsageFont, planUsageRef, planUsageSize } from "./TopNav.styles";

const { barHeight, chipHeight, unlimitedChipHeight } = planUsageSize;
const PAD = planUsageRef.contentPadding;

const PlanUsage = () => {
  const accountName = useAppSelector(selectAccount);
  const [accountDetails, setAccountDetails] = useState<AccountDetails>(
    {} as AccountDetails,
  );
  const [currentPlan, setCurrentPlan] = useState<AppPlan>({} as AppPlan);
  const [loading, setLoading] = useState<boolean>(true);

  const CertifiedSignedStats = accountDetails?.appPlanLimitList?.find(
    (plan: any) => plan.entity === strings.notariusVerifiedEnvelope,
  );

  const theme = useTheme();
  const isPlanExpired = accountDetails?.validTill
    ? new Date(accountDetails.validTill) < new Date()
    : true;

  const hasBillingFetchPermission = hasAccessTo(
    strings.resources.billing,
    strings.fetchPermission,
  );

  useEffect(() => {
    if (hasBillingFetchPermission) {
      fetchAccountPlanDetails();
    }
  }, []);

  const fetchAccountPlanDetails = async () => {
    try {
      setLoading(true);
      const [currentPlan, accountDetails] = await Promise.all([
        fetchCurrentPlan(accountName),
        fetchAccountDetails(),
      ]);
      setCurrentPlan(currentPlan as AppPlan);
      setAccountDetails(accountDetails);
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const plan = {
    name: currentPlan.name,
    price: currentPlan.price,
    validTill: new Date(accountDetails.validTill)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, " "),
    certifiedEnvelopeUsed: CertifiedSignedStats?.current ?? 0,
    certifiedEnvelopeLimit: CertifiedSignedStats?.limit ?? 0,
  };

  const certifiedRemaining =
    plan.certifiedEnvelopeLimit - plan.certifiedEnvelopeUsed;

  const certifiedUsagePercent = isUnlimitedLimit(plan.certifiedEnvelopeLimit)
    ? 0
    : plan.certifiedEnvelopeLimit > 0
      ? (plan.certifiedEnvelopeUsed / plan.certifiedEnvelopeLimit) * 100
      : 0;

  const getStatusChipColor = () => {
    const daysUntilExpiry = Math.ceil(
      (new Date(accountDetails.validTill).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24),
    );
    if (daysUntilExpiry <= 7) return "error";
    if (daysUntilExpiry <= 30) return "warning";
    return "success";
  };

  // No border or radius of its own — the popover Paper is the container and
  // already draws both. The divider runs full-bleed past the text inset, so the
  // padding lives on this box and the divider cancels it with negative margins.
  const rootSx = {
    minWidth: { xs: 0, sm: 340 },
    maxWidth: 400,
    padding: `${PAD}px`,
    backgroundColor: theme.palette.common.white,
  } as const;

  const dividerSx = {
    my: "16px",
    mx: `-${PAD}px`,
    borderColor: planUsageRef.divider,
  } as const;

  if (loading) {
    return (
      <Box sx={rootSx}>
        {/* Header: overline + status pill */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Skeleton variant="text" width={92} height={12} />
          <Skeleton
            variant="rounded"
            width={64}
            height={chipHeight}
            sx={{ borderRadius: "999px" }}
          />
        </Box>
        {/* Plan name + validity on one line */}
        <Box display="flex" alignItems="center" gap={1.25} mt="10px">
          <Skeleton variant="text" width={104} height={22} />
          <Skeleton variant="text" width={128} height={14} />
        </Box>
        <Divider sx={dividerSx} />
        {/* Usage row + meter */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Skeleton variant="text" width={96} height={14} />
          <Skeleton variant="text" width={132} height={14} />
        </Box>
        <Skeleton
          variant="rounded"
          height={barHeight}
          sx={{ borderRadius: "999px", mt: "10px" }}
        />
        {/* Footer note, two lines */}
        <Box mt="14px">
          <Skeleton variant="text" height={13} />
          <Skeleton variant="text" width="42%" height={13} />
        </Box>
      </Box>
    );
  }

  if (isPlanExpired) {
    return (
      <Box sx={rootSx}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          py={1}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: theme.palette.primary.alpha12,
              color: "primary.main",
              mb: 1.5,
            }}
          >
            <ErrorOutlineRoundedIcon />
          </Box>
          <Typography
            sx={{
              fontSize: planUsageFont.planName,
              fontWeight: 700,
              color: planUsageRef.heading,
              mb: 0.5,
            }}
          >
            No Active Subscription
          </Typography>
          <Typography
            sx={{
              fontSize: planUsageFont.note,
              color: planUsageRef.metaText,
              lineHeight: 1.55,
            }}
          >
            {`Looks like your last subscription ended on `}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: planUsageRef.heading }}
            >
              {plan.validTill}
            </Box>
            {`. Pick a plan to get back to creating, sending, and tracking with ease.`}
          </Typography>
        </Box>
      </Box>
    );
  }

  const isUnlimited = isUnlimitedLimit(plan.certifiedEnvelopeLimit);
  const usagePercentRounded = Math.min(Math.round(certifiedUsagePercent), 100);
  const hasValidTill =
    isTruthy(plan.validTill) && plan.validTill !== "Invalid Date";
  const statusColor = theme.palette[getStatusChipColor()].main;
  const isActive = getStatusChipColor() === "success";

  // Unlimited plans have no percentage to draw, so the meter is replaced by an
  // "∞ Unlimited" band (below) instead of rendering a misleading empty bar.
  const fillWidth = `max(${barHeight}px, ${usagePercentRounded}%)`;

  // The reference only shows a healthy meter; the near-limit escalation the
  // popover has always had is kept so a capped plan still warns before it runs out.
  const fillColor =
    certifiedUsagePercent >= 90
      ? theme.palette.error.main
      : certifiedUsagePercent >= 70
        ? theme.palette.warning.main
        : planUsageRef.progressFill;

  // The row above already shows "used / limit", so the sentence speaks to what
  // the user can still do rather than repeating the numbers.
  const remaining = Math.max(certifiedRemaining, 0);
  const getUsageSummary = () => {
    if (isUnlimited) {
      return `Send as many sign requests as you need${
        isTruthy(plan.name) ? ` on your ${plan.name} plan` : ""
      }.`;
    }
    if (remaining === 0) {
      return `You've used all ${formatLimit(
        plan.certifiedEnvelopeLimit,
      )} sign requests. Upgrade to keep sending.`;
    }
    if (remaining === 1) {
      return "1 sign request left — make it count.";
    }
    return `${formatCount(remaining)} sign requests left.`;
  };
  const usageSummary = getUsageSummary();

  return (
    <Box sx={rootSx}>
      {/* Header: overline + plan status */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Typography
          sx={{
            fontSize: planUsageFont.overline,
            fontWeight: 700,
            letterSpacing: "0.09em",
            textTransform: "uppercase",
            color: planUsageRef.metaText,
          }}
        >
          Usage &amp; Plan
        </Typography>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            height: chipHeight,
            padding: "0 11px",
            borderRadius: "999px",
            fontSize: planUsageFont.chip,
            fontWeight: 700,
            letterSpacing: "0.06em",
            whiteSpace: "nowrap",
            color: isActive ? planUsageRef.activeChipText : statusColor,
            backgroundColor: isActive
              ? planUsageRef.activeChipBg
              : theme.palette[getStatusChipColor()].alpha12,
          }}
        >
          ACTIVE
        </Box>
      </Box>

      {/* Plan name + validity, sharing one baseline */}
      <Box
        display="flex"
        alignItems="baseline"
        gap={1.25}
        mt="6px"
        minWidth={0}
      >
        <Typography
          noWrap
          sx={{
            fontSize: planUsageFont.planName,
            fontWeight: 700,
            color: planUsageRef.heading,
            lineHeight: 1.3,
          }}
        >
          {isTruthy(plan.name) ? plan.name : "—"}
        </Typography>
        {hasValidTill && (
          <Typography
            noWrap
            sx={{
              fontSize: planUsageFont.validity,
              fontWeight: 500,
              color: planUsageRef.metaText,
            }}
          >
            Valid until {plan.validTill}
          </Typography>
        )}
      </Box>

      <Divider sx={dividerSx} />

      {/* Usage: label + count on one row, meter beneath. A meter needs a ceiling
          to fill against, so unlimited plans carry an "∞ Unlimited" pill on this
          row instead and drop the rail entirely. */}
      <Box
        display="flex"
        alignItems={isUnlimited ? "center" : "baseline"}
        justifyContent="space-between"
        gap={1}
      >
        <Typography
          sx={{
            fontSize: planUsageFont.rowLabel,
            fontWeight: 700,
            color: planUsageRef.heading,
          }}
        >
          Sign requests
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {isUnlimited && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                height: unlimitedChipHeight,
                padding: "0 9px",
                borderRadius: "999px",
                fontSize: planUsageFont.chip,
                fontWeight: 700,
                whiteSpace: "nowrap",
                color: planUsageRef.unlimitedChipText,
                backgroundColor: planUsageRef.unlimitedChipBg,
              }}
            >
              <UnlimitedLabel iconSize={13} />
            </Box>
          )}
          <Typography
            noWrap
            sx={{
              fontSize: planUsageFont.rowValue,
              fontWeight: 600,
              color: planUsageRef.valueText,
            }}
          >
            {isUnlimited
              ? `${formatCount(plan.certifiedEnvelopeUsed)} used`
              : `${formatCount(plan.certifiedEnvelopeUsed)} / ${formatLimit(
                  plan.certifiedEnvelopeLimit,
                )} used`}
          </Typography>
        </Box>
      </Box>

      {!isUnlimited && (
        <Box
          role="progressbar"
          aria-valuenow={usagePercentRounded}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Sign request usage"
          sx={{
            mt: "10px",
            height: barHeight,
            borderRadius: "999px",
            backgroundColor: planUsageRef.progressTrack,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: fillWidth,
              borderRadius: "999px",
              backgroundColor: fillColor,
            }}
          />
        </Box>
      )}

      <Typography
        sx={{
          mt: isUnlimited ? "10px" : "14px",
          fontSize: planUsageFont.note,
          color: planUsageRef.metaText,
          lineHeight: 1.55,
        }}
      >
        {usageSummary}
      </Typography>
    </Box>
  );
};

export default PlanUsage;
